import type { ComponentProps } from 'react'

import type { FilterOption } from '@models/Filter'

import { FilterSelectString } from './FilterSelectString'

export { FilterSelectCode }

type Props<
  TFilters extends Record<string, any>,
  K extends keyof TFilters,
> = Omit<
  ComponentProps<typeof FilterSelectString<string, TFilters, K>>,
  'options' | 'getOptionLabel' | 'groupBy'
> & {
  options: (FilterOption & { group?: string })[]
}

/**
 * Select for filters that store the code of the selected option(s) instead of
 * the option itself, e.g. filters in the URL. Groups options with a `group`.
 */
const FilterSelectCode = <
  TFilters extends Record<string, any>,
  K extends keyof TFilters,
>({
  options,
  ...rest
}: Props<TFilters, K>) => {
  const byCode = new Map(options.map((option) => [option.code, option]))
  const getLabel = (code: string) => byCode.get(code)?.label ?? code
  const getGroup = options.some(({ group }) => group)
    ? (code: string) => byCode.get(code)?.group ?? ''
    : undefined

  return (
    <FilterSelectString
      {...rest}
      options={options.map(({ code }) => code)}
      getOptionLabel={getLabel}
      groupBy={getGroup}
    />
  )
}
